import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'
import { flatCols } from './useMinOneDayData'
import { yesterdayStr } from '../utils/timeUtils'

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate()
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

// isAktual: ignoruje year/month a použije klouzavé okno posledních 30 dní končící VČEREJŠKEM
// (dnešní den se nestahuje — den neskončil, denní data nejsou kompletní)
export function useDlyOneMonthData(ghId, year, month, columns, isAktual = false, enabled = true) {
  const allCols     = flatCols(columns)
  const tsIds       = allCols.map(c => c.tsId)

  const mm       = String(month).padStart(2, '0')
  const lastDay  = String(daysInMonth(year, month)).padStart(2, '0')

  let begin_date, end_date, rangeStart, rangeEnd
  if (isAktual) {
    const now = new Date()
    // rangeEnd = včerejšek (dnešek 00:00 − 1 den); okno = posledních 30 dní do včerejška včetně.
    rangeEnd = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1))
    rangeStart = new Date(rangeEnd.getTime() - 30 * 86400000)
    begin_date = `${rangeStart.toISOString().slice(0, 10)}T00:00:00Z`
    end_date   = `${rangeEnd.toISOString().slice(0, 10)}T23:59:59Z`
  } else {
    begin_date = `${year}-${mm}-01T00:00:00Z`
    // U aktuálního měsíce nekonči dál než včerejšek (dnešní denní data nejsou kompletní).
    const monthEnd = `${year}-${mm}-${lastDay}T23:59:59Z`
    const yEnd = `${yesterdayStr()}T23:59:59Z`
    end_date = monthEnd > yEnd ? yEnd : monthEnd
  }

  const queryKey = ['dlyOneMonthData', ghId, year, month, tsIds, isAktual]

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const responses = await fetchTsIds(
        tsIds,
        tsId => api.get(`/daily_ts?ts_id=${tsId}&gh_id=${ghId}&begin_date=${begin_date}&end_date=${end_date}`),
      )
      const result = Object.fromEntries(tsIds.map((tsId, i) => [tsId, responses[i]?.data ?? []]))
      const toFill = responses.flatMap(r => r?.to_fill ?? [])
      return { result, toFill }
    },
    enabled: !!ghId && enabled,
    staleTime: 5 * 60 * 1000,
  })

  useToFill(queryKey, data, (result, resp, item) => {
    const tsId = item.params?.ts_id
    if (!tsId || !(tsId in result)) return result
    return { ...result, [tsId]: resp.data }
  }, { gran: 'DLY', live: isAktual })

  // dataMap: "YYYY-MM-DD" → { tsId: value, ... }
  // labelMap: "YYYY-MM-DD" → "D." — všechny dny měsíce/okna, i bez dat
  const dataMap  = new Map()
  const labelMap = new Map()

  if (isAktual) {
    for (let ms = rangeStart.getTime(); ms <= rangeEnd.getTime(); ms += 86400000) {
      const d = new Date(ms)
      const key = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
      labelMap.set(key, `${d.getUTCDate()}.${d.getUTCMonth() + 1}.`)
    }
  } else {
    // Předplnit labelMap všemi dny měsíce
    const totalDays = daysInMonth(year, month)
    for (let d = 1; d <= totalDays; d++) {
      const key = `${year}-${mm}-${String(d).padStart(2, '0')}`
      labelMap.set(key, `${d}.`)
    }
  }

  if (data?.result) {
    allCols.forEach(col => {
      ;(data.result[col.tsId] ?? []).forEach(row => {
        if (row.dt == null) return
        const d   = new Date(row.dt)
        const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
        const entry = dataMap.get(key) ?? {}
        entry[col.tsId] = row.value ?? null
        dataMap.set(key, entry)
      })
    })
  }

  if (isError) {
    console.error('[useDlyOneMonthData] fetch failed', { ghId, year, month, error })
  }

  return { dataMap, labelMap, isLoading, isError, error }
}
